import { connectSocket, disconnectSocket } from '@/socket';
import { ReactNode, useEffect, useState } from 'react';
import { socketContext } from './SocketContext';
import { useQueryClient } from '@tanstack/react-query';
import { endpoints } from './endpoints';
import { toast } from 'react-toastify';

export default function Socket({ children }: { children: ReactNode }) {
  const [isConnected, setIsConnected] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const queryClient = useQueryClient();

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) return;

    const socket = connectSocket();

    const handleConnect = () => {
      console.log('[Socket] ✅ Connected:', socket.id);
      setIsConnected(true);
    };

    const handleDisconnect = (reason: string) => {
      console.log('[Socket] ❌ Disconnected:', reason);
      setIsConnected(false);
    };

    const handleError = (error: Error) => {
      console.error('[Socket] ⚠️ Error:', error);
    };

    // ── Generic notification handler ───────────────────────────────────
    const handleNotification = (data: any) => {
      console.log('[Socket] 🔔 Notification received:', data);
      setUnreadCount(prev => prev + 1);
      // Invalidate both queries so badge and list refresh immediately
      queryClient.invalidateQueries({ queryKey: [endpoints.getUnreadCount] });
      queryClient.invalidateQueries({ queryKey: [endpoints.getAdminNotifications] });
      const title = data?.title || data?.notification?.title || 'New Notification';
      const body  = data?.body  || data?.notification?.body  || '';
      toast.info(`🔔 ${title}${body ? `: ${body}` : ''}`, { autoClose: 5000 });
    };

    // ── Ticket events ──────────────────────────────────────────────────
    const handleNewTicket = (data: any) => {
      console.log('[Socket] 📋 New ticket:', data);
      queryClient.invalidateQueries({ queryKey: [endpoints.getAllTickets] });
      toast.info('📋 A new ticket has been submitted', { autoClose: 4000 });
    };

    const handleTicketUpdated = (data: any) => {
      console.log('[Socket] 📋 Ticket updated:', data);
      queryClient.invalidateQueries({ queryKey: [endpoints.getAllTickets] });
    };

    // ── Book events ────────────────────────────────────────────────────
    const handleBookStatusChanged = (data: any) => {
      console.log('[Socket] 📚 Book status changed:', data);
      queryClient.invalidateQueries({ queryKey: [endpoints.getAllBooks] });
      const status = data?.status || data?.bookStatus || 'updated';
      toast.info(`📚 Book status changed to: ${status}`, { autoClose: 4000 });
    };

    // ── Debug: log ALL events to find the real event name ─────────────
    // This monkey-patches the socket's onevent so every event is logged
    const originalOnevent = (socket as any).onevent?.bind(socket);
    if (originalOnevent) {
      (socket as any).onevent = (packet: any) => {
        const args = packet.data || [];
        console.log('[Socket] 📡 RAW event:', args[0], '→ payload:', args.slice(1));
        originalOnevent(packet);
      };
    }

    // Register known event names — cover all possible variants the backend may use
    const notifEvents = [
      'notification',
      'new_notification',
      'receive_notification',
      'admin_notification',
      'push_notification',
    ];

    socket.on('connect',    handleConnect);
    socket.on('disconnect', handleDisconnect);
    socket.on('error',      handleError);
    notifEvents.forEach(ev => socket.on(ev, handleNotification));
    socket.on('new_ticket',           handleNewTicket);
    socket.on('ticket_created',       handleNewTicket);
    socket.on('ticket_updated',       handleTicketUpdated);
    socket.on('book_status_changed',  handleBookStatusChanged);
    socket.on('book_approved',        handleBookStatusChanged);

    if (socket.connected) setIsConnected(true);

    return () => {
      socket.off('connect',    handleConnect);
      socket.off('disconnect', handleDisconnect);
      socket.off('error',      handleError);
      notifEvents.forEach(ev => socket.off(ev, handleNotification));
      socket.off('new_ticket',           handleNewTicket);
      socket.off('ticket_created',       handleNewTicket);
      socket.off('ticket_updated',       handleTicketUpdated);
      socket.off('book_status_changed',  handleBookStatusChanged);
      socket.off('book_approved',        handleBookStatusChanged);
      // Restore original onevent
      if (originalOnevent) {
        (socket as any).onevent = originalOnevent;
      }
    };
  }, [queryClient]);

  return (
    <socketContext.Provider value={{ isConnected, unreadCount, setUnreadCount }}>
      {children}
    </socketContext.Provider>
  );
}

export { disconnectSocket };
